import { useState, useRef, useEffect, useCallback } from 'react';
import { fileToBase64, generateMessageId } from '@/utils/extractionUtils';
import {
  WEBSOCKET_PING_INTERVAL,
  WEBSOCKET_RECONNECT_MAX,
  BOOKING_EXTRACTION,
} from '@/config/timeouts';

export interface Passenger {
  first_name: string;
  last_name: string;
}

export interface FlightSegment {
  carrier: string;
  flight_no: string;
  operating_carrier?: string;
  dep_iata: string;
  arr_iata: string;
  dep_date: string;
  dep_time: string;
  arr_date?: string; // Derived by backend from dep_date + times
  arr_time: string;
  booking_class_category: string;
}

export interface ExtractedBookingData {
  pnr: string;
  booking_date?: string;
  passengers: Passenger[];
  flight_segments: FlightSegment[];
}

export interface ExtractionProgress {
  attempt: number;
  maxAttempts: number;
  message: string;
}

export type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'error';

// Configuration (imported from centralized timeouts)
const PING_INTERVAL_MS = WEBSOCKET_PING_INTERVAL;
const MAX_BACKOFF_MS = WEBSOCKET_RECONNECT_MAX;
const EXTRACTION_TIMEOUT_MS = BOOKING_EXTRACTION;

export interface UseBookingExtractionReturn {
  extractBooking: (file: File) => Promise<void>;
  isExtracting: boolean;
  progress: ExtractionProgress | null;
  extractedData: ExtractedBookingData | null;
  error: string | null;
  reset: () => void;
  wsStatus: WsStatus;
  reconnectAttempt: number;
  retryConnection: () => void;
}

export function useBookingExtraction(): UseBookingExtractionReturn {
  // WebSocket ref
  const wsRef = useRef<WebSocket | null>(null);

  // Extraction tracking
  const messageIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reconnection tracking
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timer | null>(null);

  // Pending extraction for resumption after reconnect
  const pendingExtractionRef = useRef<{ file: File; messageId: string } | null>(null);

  // Track if we're intentionally closing
  const isIntentionalCloseRef = useRef(false);

  // State
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState<ExtractionProgress | null>(null);
  const [extractedData, setExtractedData] = useState<ExtractedBookingData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [wsStatus, setWsStatus] = useState<WsStatus>('disconnected');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  // Clear pending extraction (called on success or error)
  const clearPendingExtraction = useCallback(() => {
    pendingExtractionRef.current = null;
  }, []);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);

      // Handle pong response (keep-alive)
      if (data.type === 'pong') {
        return;
      }

      // Only process messages for our current extraction
      if (data.message_id !== messageIdRef.current) return;

      console.log('[BookingExtraction] WebSocket message:', data.type, data);

      switch (data.type) {
        case 'extraction_progress':
          setProgress({
            attempt: data.attempt || 1,
            maxAttempts: data.max_attempts || 3,
            message: `Analyzing your booking... (attempt ${data.attempt}/${data.max_attempts})`,
          });
          break;

        case 'extraction_retry':
          setProgress((prev) => ({
            attempt: (data.attempt || 0) + 1,
            maxAttempts: prev?.maxAttempts || 3,
            message: `Retrying extraction... (attempt ${(data.attempt || 0) + 1}/${prev?.maxAttempts || 3})`,
          }));
          break;

        case 'extraction_complete':
          // Clear timeout to prevent false timeout error
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          messageIdRef.current = null;
          clearPendingExtraction();
          setIsExtracting(false);
          setProgress(null);
          setExtractedData(data.data);
          setError(null);
          break;

        case 'extraction_error':
          // Clear timeout on error too
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          messageIdRef.current = null;
          clearPendingExtraction();
          setIsExtracting(false);
          setProgress(null);
          setError(data.error || 'Extraction failed');
          break;

        case 'loading':
          setProgress((prev) => ({
            attempt: prev?.attempt || 1,
            maxAttempts: prev?.maxAttempts || 3,
            message: data.message || 'Processing...',
          }));
          break;

        default:
          break;
      }
    } catch (e) {
      console.error('[BookingExtraction] Error parsing WebSocket message:', e);
    }
  }, [clearPendingExtraction]);

  // Resend pending extraction after reconnection
  const resendPendingExtraction = useCallback(async () => {
    const pending = pendingExtractionRef.current;
    if (!pending || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    console.log('[BookingExtraction] Resuming pending extraction:', pending.messageId);

    try {
      // Update progress to show we're resuming
      setProgress({
        attempt: 1,
        maxAttempts: 3,
        message: 'Resuming extraction...',
      });

      const base64Data = await fileToBase64(pending.file);
      const message = {
        file: {
          name: pending.file.name,
          type: pending.file.type,
          data: base64Data,
        },
        message_id: pending.messageId,
        extract_booking_confirmation: true,
      };

      wsRef.current.send(JSON.stringify(message));
      console.log('[BookingExtraction] Resent pending extraction:', pending.messageId);

      // Reset extraction timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (messageIdRef.current === pending.messageId) {
          setIsExtracting(false);
          setProgress(null);
          setError('Extraction timed out. Please try again.');
          messageIdRef.current = null;
          clearPendingExtraction();
        }
      }, EXTRACTION_TIMEOUT_MS);
    } catch (err) {
      console.error('[BookingExtraction] Failed to resend pending extraction:', err);
      pendingExtractionRef.current = null;
      messageIdRef.current = null;
      setError('Failed to resume extraction. Please try again.');
      setIsExtracting(false);
      setProgress(null);
    }
  }, [clearPendingExtraction]);

  // Start ping interval
  const startPingInterval = useCallback(() => {
    // Clear existing interval if any
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);
  }, []);

  // Stop ping interval
  const stopPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    // Don't reconnect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN ||
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      setWsStatus('connecting');
      const WS_HOST = import.meta.env.VITE_WS_HOST || 'localhost:8000';
      const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsUrl = `${WS_PROTOCOL}://${WS_HOST}/ws/chat/`;

      console.log('[BookingExtraction] Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[BookingExtraction] WebSocket connected');
        setWsStatus('connected');

        // Reset reconnection counter on successful connection
        reconnectAttemptsRef.current = 0;
        setReconnectAttempt(0);

        // Start keep-alive pings
        startPingInterval();

        // Resume pending extraction if exists
        if (pendingExtractionRef.current && messageIdRef.current) {
          resendPendingExtraction();
        }
      };

      ws.onclose = (event) => {
        console.log('[BookingExtraction] WebSocket disconnected, code:', event.code);

        // Stop ping interval
        stopPingInterval();

        // Clean up WebSocket ref
        wsRef.current = null;

        // Check if this was an intentional close
        if (isIntentionalCloseRef.current || event.code === 1000) {
          setWsStatus('disconnected');
          isIntentionalCloseRef.current = false;
          return;
        }

        // Auto-reconnect with exponential backoff
        const currentAttempt = reconnectAttemptsRef.current;
        setWsStatus('reconnecting');
        setReconnectAttempt(currentAttempt + 1);

        const delay = Math.min(1000 * Math.pow(2, currentAttempt), MAX_BACKOFF_MS);
        console.log(`[BookingExtraction] Reconnecting in ${delay}ms (attempt ${currentAttempt + 1})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current++;
          connectWebSocket();
        }, delay);
      };

      ws.onerror = (err) => {
        console.error('[BookingExtraction] WebSocket error:', err);
        // Don't set error status here - let onclose handle reconnection
        // Only set error if we're not going to reconnect
      };

      ws.onmessage = handleWebSocketMessage;
    } catch (err) {
      console.error('[BookingExtraction] WebSocket connection error:', err);
      setWsStatus('error');
    }
  }, [handleWebSocketMessage, startPingInterval, stopPingInterval, resendPendingExtraction]);

  // Manual retry connection
  const retryConnection = useCallback(() => {
    // Clear any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Reset counters
    reconnectAttemptsRef.current = 0;
    setReconnectAttempt(0);

    // Close existing connection if any
    if (wsRef.current) {
      isIntentionalCloseRef.current = true;
      wsRef.current.close();
      wsRef.current = null;
    }

    // Connect fresh
    connectWebSocket();
  }, [connectWebSocket]);

  // Initialize WebSocket on mount
  useEffect(() => {
    connectWebSocket();

    return () => {
      // Cleanup on unmount
      isIntentionalCloseRef.current = true;

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      stopPingInterval();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
        wsRef.current = null;
      }
    };
  }, [connectWebSocket, stopPingInterval]);

  // Update WebSocket message handler when it changes
  useEffect(() => {
    if (wsRef.current) {
      wsRef.current.onmessage = handleWebSocketMessage;
    }
  }, [handleWebSocketMessage]);

  // Extract booking from file
  const extractBooking = useCallback(async (file: File) => {
    setError(null);
    setExtractedData(null);
    setIsExtracting(true);
    setProgress({
      attempt: 1,
      maxAttempts: 3,
      message: 'Preparing your file...',
    });

    try {
      // Convert file to base64
      const base64Data = await fileToBase64(file);

      setProgress({
        attempt: 1,
        maxAttempts: 3,
        message: 'Starting extraction...',
      });

      // Check WebSocket connection
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        // Store for retry after reconnection
        const msgId = generateMessageId();
        messageIdRef.current = msgId;
        pendingExtractionRef.current = { file, messageId: msgId };

        // Trigger reconnection if not already reconnecting
        if (wsStatus !== 'reconnecting' && wsStatus !== 'connecting') {
          retryConnection();
        }

        setProgress({
          attempt: 1,
          maxAttempts: 3,
          message: 'Waiting for connection...',
        });
        return;
      }

      // Generate message ID for tracking
      const msgId = generateMessageId();
      messageIdRef.current = msgId;

      // Store pending extraction for potential retry
      pendingExtractionRef.current = { file, messageId: msgId };

      // Send extraction request via WebSocket
      const message = {
        file: {
          name: file.name,
          type: file.type,
          data: base64Data,
        },
        message_id: msgId,
        extract_booking_confirmation: true,
      };

      wsRef.current.send(JSON.stringify(message));
      console.log('[BookingExtraction] Sent extraction request:', msgId);

      // Set timeout for extraction
      timeoutRef.current = setTimeout(() => {
        if (messageIdRef.current === msgId) {
          setIsExtracting(false);
          setProgress(null);
          setError('Extraction timed out. Please try again.');
          messageIdRef.current = null;
          clearPendingExtraction();
        }
      }, EXTRACTION_TIMEOUT_MS);
    } catch (err) {
      console.error('[BookingExtraction] Error:', err);
      setIsExtracting(false);
      setProgress(null);
      setError(err instanceof Error ? err.message : 'Failed to process file');
      clearPendingExtraction();
    }
  }, [wsStatus, retryConnection, clearPendingExtraction]);

  // Reset state
  const reset = useCallback(() => {
    setIsExtracting(false);
    setProgress(null);
    setExtractedData(null);
    setError(null);
    messageIdRef.current = null;
    clearPendingExtraction();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [clearPendingExtraction]);

  return {
    extractBooking,
    isExtracting,
    progress,
    extractedData,
    error,
    reset,
    wsStatus,
    reconnectAttempt,
    retryConnection,
  };
}
